import { Fragment, useEffect, useState } from 'react';
import { useOffline } from '../offline/context.js';
import {
  LINK_KIND_LABELS,
  searchRecords,
  type LinkItem,
  type LinkKind,
  type SearchResult,
} from './LinkPicker.js';

/**
 * A search that lists its results in the window (prototype: input and
 * .picker with .lp-it rows): New lead, Add a contact to a lead, Link to an
 * existing lead. The portal's records, then HubSpot's (from 2 letters).
 */
export function RecordSearch({
  label,
  placeholder,
  kinds,
  exclude = [],
  onPick,
  inline = false,
}: {
  label: string;
  placeholder: string;
  kinds: LinkKind[];
  /** Records already chosen, left out of the list. */
  exclude?: string[];
  onPick(item: LinkItem): void;
  /**
   * A field among others of a form (Meeting, Nov 4, 2026): no box until
   * something is typed, then the results in a list under the field (as
   * Linked to), and the cursor left where it is.
   */
  inline?: boolean;
}) {
  const { store } = useOffline();
  const [term, setTerm] = useState('');
  const [result, setResult] = useState<SearchResult | null>(null);
  const kindsKey = kinds.join(',');

  useEffect(() => {
    const q = term.trim();
    if (!q) return;
    let current = true;
    const timer = setTimeout(() => {
      void searchRecords(q, kindsKey.split(',') as LinkKind[], store).then(
        (r) => current && setResult(r),
      );
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [term, kindsKey, store]);

  const shown = term.trim() ? result : null;
  const items = shown?.items.filter((i) => !i.id || !exclude.includes(i.id)) ?? [];
  const header = (item: LinkItem, i: number) =>
    i === 0 || item.source !== items[i - 1]!.source ? (
      <div className="lp-h">{item.source === 'hubspot' ? 'In HubSpot' : 'In the portal'}</div>
    ) : null;

  return (
    <>
      <input
        type="search"
        aria-label={label}
        placeholder={placeholder}
        autoComplete="off"
        autoFocus={!inline}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
      />
      <div
        className={inline ? 'lp-drop' : 'picker'}
        style={inline ? undefined : { maxHeight: 'none' }}
        hidden={inline && !term.trim()}
        role="listbox"
        aria-label={label}
      >
        {!term.trim() ? (
          inline ? null : (
            <div className="lp-msg">Type a name, a company or an email.</div>
          )
        ) : !shown ? (
          <div className="lp-msg">Searching…</div>
        ) : items.length === 0 ? (
          <div className="lp-msg">Nothing found for “{term.trim()}”.</div>
        ) : (
          <>
            {items.map((item, i) => (
              <Fragment key={`${item.kind}:${item.id ?? item.hubspotId ?? item.label}`}>
                {header(item, i)}
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="lp-it"
                  onClick={() => onPick(item)}
                >
                  <span className={`lp-k ${item.kind}`}>{LINK_KIND_LABELS[item.kind]}</span>
                  <span>
                    <span className="tt">{item.label}</span>
                    <small>{item.sub ?? ''}</small>
                  </span>
                </button>
              </Fragment>
            ))}
            {shown.total > items.length && (
              <div className="lp-foot">
                Showing {items.length} of {shown.total}. Keep typing to narrow.
              </div>
            )}
          </>
        )}
      </div>
      {shown?.hubspot === 'unavailable' && (
        <p className="note" style={{ margin: 0 }}>
          HubSpot is not answering: only the portal&rsquo;s records are shown.
        </p>
      )}
    </>
  );
}
